import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDeliveredOrdersDocumentation,
  type OrderWithCustomer,
} from "@/hooks/useDeliveredOrdersDocumentation";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { BolViewerDialog } from "@/components/orders/BolViewerDialog";
import { TenderViewerDialog } from "@/components/orders/TenderViewerDialog";
import { OrderEditDialog } from "@/components/orders/OrderEditDialog";
import { OrderDetailSheet } from "@/components/orders/OrderDetailSheet";
import { InlineEditCell } from "@/components/orders/InlineEditCell";
import { ArrowRight, ArrowUpRight, ArrowDownRight, FileText, Pencil, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { Order } from "@/types/tms";
import { DashboardPagination, usePagination } from "@/components/dashboard/DashboardPagination";
import { useOrderLegSummaries } from "@/hooks/useOrderLegSummaries";
import { DataCard, DetailRow } from "@/components/ui/data-card";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";

/**
 * Mobile/tablet card for the Unbillable Orders dashboard tab.
 * Mirrors the columns the table renders.
 */
function DocGapOrderCard({
  order,
  deliveryTime,
  missingRequirements,
  legs,
  showTenderId,
  onClick,
}: {
  order: OrderWithCustomer;
  deliveryTime: string;
  missingRequirements?: string[];
  showTenderId?: boolean;
  onClick: () => void;
  legs?: any[];
}) {
  return (
    <DataCard onClick={onClick}>
      <DataCard.Header>
        <div className="min-w-0 flex-1">
          <span className="font-mono font-bold text-lg leading-none text-foreground truncate">
            {order.unique_id}
          </span>
        </div>
      </DataCard.Header>
      {order.billing_party?.organization_name && (
        <div className="text-sm text-muted-foreground truncate [font-variant-caps:all-small-caps] -mt-1">
          {order.billing_party.organization_name}
        </div>
      )}

      <DataCard.Chips>
        <OrderStatusBadge status={order.status} legs={legs} />
        {missingRequirements?.map((req) => (
          <span key={req} className="status-badge status-error">
            {req}
          </span>
        ))}
      </DataCard.Chips>

      <DataCard.Body>
        <DataCard.Field icon={ArrowUpRight}>{order.ship_from_city || "—"}</DataCard.Field>
        <DataCard.Field icon={ArrowDownRight} secondary={deliveryTime}>
          {order.ship_to_city || "—"}
        </DataCard.Field>
      </DataCard.Body>

      <div className="flex flex-col gap-2">
        <DataCard.Tile
          icon={FileText}
          label="Documents"
          value="Documents"
          trailing={
            <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <BolViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
              <TenderViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
            </span>
          }
        />
        {showTenderId ? (
          <DataCard.Tile
            icon={FileText}
            label="Tender ID"
            value={order.tender_id || <span className="text-muted-foreground font-normal">No tender ID</span>}
            trailing={
              order.tender_id ? undefined : (
                <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                  <InlineEditCell
                    orderId={order.id}
                    fieldName="tender_id"
                    currentValue={order.tender_id}
                    placeholder="Enter tender ID"
                  />
                </span>
              )
            }
          />
        ) : null}
      </div>
    </DataCard>
  );
}

/** Shown when trailer load evidence failed to load — empty moves may be listed. */
function ExclusionErrorNotice() {
  return (
    <p className="text-sm text-destructive mb-4">
      Couldn't load trailer load status, so empty-trailer moves may be listed below. Refresh to try again.
    </p>
  );
}

export function NeededDocumentationTab() {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [previewOrder, setPreviewOrder] = useState<OrderWithCustomer | null>(null);
  const navigate = useNavigate();

  const { isLoading, unbillableOrders, getMissingRequirements, exclusionError } =
    useDeliveredOrdersDocumentation();

  const formatDateTime = (dateStr: string) => {
    return format(new Date(dateStr), "MMM d, HH:mm");
  };

  const handleRowClick = (order: OrderWithCustomer, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest("input") || target.closest("[role='dialog']")) return;
    setPreviewOrder(order);
  };

  const hasOrders = !isLoading && unbillableOrders.length > 0;

  const unbillablePagination = usePagination(unbillableOrders);
  // One batched leg-status lookup for the rows on screen (never per row).
  const { legsFor } = useOrderLegSummaries(unbillablePagination.pageRows.map((o) => o.id));

  return (
    <div className="flex flex-col h-full animate-fade-in">
      {exclusionError && <ExclusionErrorNotice />}
      {hasOrders && (
        <p className="text-sm text-muted-foreground mb-4">These orders have been delivered but cannot be billed until the missing requirements are added to the order.</p>
      )}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : hasOrders ? (
        <>
          <ResponsiveTableShell
            cards={
              <div className="grid grid-cols-1 gap-3">
                {unbillablePagination.pageRows.map((order) => (
                  <DocGapOrderCard
                    key={order.id}
                    order={order}
                    deliveryTime={formatDateTime(order.requested_delivery_time)}
                    missingRequirements={getMissingRequirements(order)}
                    showTenderId
                    legs={legsFor(order.id)}
                    onClick={() => setPreviewOrder(order)}
                  />
                ))}
              </div>
            }
            table={
              <div className="rounded-lg border bg-background overflow-x-auto">
                <Table noStickyColumns>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 z-20 bg-background shadow-[1px_0_0_0_hsl(var(--border))]">Order ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Delivery Time</TableHead>
                  <TableHead>Billing Party</TableHead>
                  <TableHead>Missing Requirements</TableHead>
                  <TableHead>Tender ID</TableHead>
                  <TableHead>Documents</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unbillablePagination.pageRows.map((order) => (
                  <TableRow
                    key={order.id}
                    className="cursor-pointer group hover:bg-accent [&:hover>td:first-child]:bg-accent [&:hover>td:nth-child(2)]:bg-accent"
                    onClick={(e) => handleRowClick(order, e)}
                  >
                    <TableCell className="font-mono font-medium whitespace-nowrap sticky left-0 z-10 bg-background shadow-[1px_0_0_0_hsl(var(--border))]">
                      {order.unique_id}
                    </TableCell>
                    <TableCell>
                      <OrderStatusBadge status={order.status} legs={legsFor(order.id)} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span>{order.ship_from_city}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span>{order.ship_to_city}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDateTime(order.requested_delivery_time)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {order.billing_party?.organization_name || "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {getMissingRequirements(order).map((req) => (
                          <span
                            key={req}
                            className="status-badge status-error"
                          >
                            {req}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <InlineEditCell
                        orderId={order.id}
                        fieldName="tender_id"
                        currentValue={order.tender_id}
                        placeholder="Enter tender ID"
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <BolViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
                        <TenderViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
                </Table>
              </div>
            }
          />
          <DashboardPagination
            page={unbillablePagination.page}
            totalPages={unbillablePagination.totalPages}
            onPageChange={unbillablePagination.setPage}
            rangeStart={unbillablePagination.rangeStart}
            rangeEnd={unbillablePagination.rangeEnd}
            total={unbillablePagination.total}
          />
        </>
        ) : (
          <div className="flex flex-col items-center justify-center flex-1 text-center">
            <CheckCircle2 className="h-12 w-12 text-muted-foreground/40 mb-4" />
            <p className="text-lg font-medium text-muted-foreground">All caught up</p>
            <p className="text-sm text-muted-foreground/70 mt-1">All delivered orders meet their billing requirements</p>
          </div>
        )}

      <OrderEditDialog
        order={selectedOrder}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
      />

      <OrderDetailSheet
        order={previewOrder as any}
        open={!!previewOrder}
        onOpenChange={(open) => { if (!open) setPreviewOrder(null); }}
        onEdit={(order) => {
          setPreviewOrder(null);
          setSelectedOrder(order);
          setIsEditDialogOpen(true);
        }}
        onView={(order) => {
          const targetId = (order as any).parent_order_id || order.id;
          const legParam = (order as any).parent_order_id ? `?leg=${order.id}` : "";
          setPreviewOrder(null);
          navigate(`/orders/${targetId}${legParam}`);
        }}
      />
    </div>
  );
}
